import { MarkedTypeEnum, TestModeEnum } from '@doltech/utils/lib/constants';
import { Tooltip } from 'antd';
import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';

import { namedColors } from '../../colors';
import { colorsV2 } from '../../colors-v2';
import { getTooltipTitle } from '../../MultipleChoiceQuestionResult/MultipleChoiceQuestionResult';
import { QuestionV2 } from '../../Question/QuestionV2';
import { BlankItemResult } from '../type';

interface BlankProps extends React.HTMLAttributes<HTMLDivElement> {
  blank: BlankItemResult;
  isViewResult?: boolean;
  showQuestionNo?: boolean;
  testMode?: TestModeEnum;
  currentSelected?: boolean;
}

const Blank = (props: BlankProps) => {
  const {
    blank,
    testMode,
    isViewResult = true,
    showQuestionNo = true,
    className,
    currentSelected,
    ...rest
  } = props;
  const disableResultClassName = 'disable-result';

  const tooltipTitle = getTooltipTitle({ markedStatus: blank.markedType });

  const containerClassName = cl(className, { 'blank-selected': currentSelected });

  switch (blank.markedType) {
    case MarkedTypeEnum.WRONG:
      return (
        <Tooltip title={isViewResult ? tooltipTitle : ''}>
          <AnswerContainer
            className={cl(containerClassName, {
              'is-view-test': testMode === TestModeEnum.VIEW_TEST_QUESTION,
              'is-view-explanation': testMode === TestModeEnum.EXPLANATION,
            })}
            {...rest}
          >
            <Answer
              className={isViewResult ? 'in-correct' : disableResultClassName}
              order={showQuestionNo ? blank.no : null}
              content={blank.userAnswer}
              markedStatus={blank.markedType}
              isViewResult={isViewResult}
              testMode={testMode}
            />
            {isViewResult && (
              <Answer
                className="correct"
                content={blank.correctAnswer}
                isViewResult={isViewResult}
                testMode={testMode}
              />
            )}
          </AnswerContainer>
        </Tooltip>
      );
    case MarkedTypeEnum.MISSING:
      return (
        <Tooltip title={isViewResult ? tooltipTitle : ''}>
          <AnswerContainer
            className={cl(containerClassName, {
              'is-view-test': testMode === TestModeEnum.VIEW_TEST_QUESTION,
              'is-view-explanation': testMode === TestModeEnum.EXPLANATION,
            })}
            {...rest}
          >
            <Answer
              className={isViewResult ? 'missing' : disableResultClassName}
              order={showQuestionNo ? blank.no : null}
              content="            "
              markedStatus={blank.markedType}
              isViewResult={isViewResult}
              testMode={testMode}
            />
            {isViewResult && (
              <Answer
                className={cl('correct', { 'is-view-result': isViewResult })}
                content={blank.correctAnswer}
                isViewResult={isViewResult}
                testMode={testMode}
              />
            )}
          </AnswerContainer>
        </Tooltip>
      );
    default:
      return (
        <Tooltip title={isViewResult ? tooltipTitle : ''}>
          <AnswerContainer
            className={cl(containerClassName, {
              'is-view-test': testMode === TestModeEnum.VIEW_TEST_QUESTION,
              'is-view-explanation': testMode === TestModeEnum.EXPLANATION,
            })}
            {...rest}
          >
            <Answer
              className={isViewResult ? 'correct' : disableResultClassName}
              order={showQuestionNo ? blank.no : null}
              content={blank.correctAnswer}
              markedStatus={blank.markedType}
              isViewResult={isViewResult}
              testMode={testMode}
            />
          </AnswerContainer>
        </Tooltip>
      );
  }
};

interface AnswerProps {
  order?: number;
  content: string;
  className: string;
  markedStatus?: MarkedTypeEnum;
  isViewResult?: boolean;
  icon?: React.ReactNode;
  testMode?: TestModeEnum;
}

const getColorByStatus = (status) => {
  switch (status) {
    case MarkedTypeEnum.CORRECT:
      return colorsV2.green80;
    case MarkedTypeEnum.WRONG:
    case MarkedTypeEnum.MISSING:
    default:
      return colorsV2.red80;
  }
};

const StrikeDiv = styled.span<any>`
  color: ${colorsV2.black100};
  border-bottom: 2px solid ${(p) => getColorByStatus(p.status)};
  line-height: 20px;
  white-space: pre-wrap;
  word-break: break-word;
`;
const Answer = ({
  order,
  content,
  icon,
  markedStatus = MarkedTypeEnum.CORRECT,
  isViewResult,
  className,
  testMode,
  ...rest
}: AnswerProps) => {
  return (
    <Container
      className={cl(
        {
          'is-view-test': testMode === TestModeEnum.VIEW_TEST_QUESTION,
          'is-view-explanation': testMode === TestModeEnum.EXPLANATION,
        },
        className
      )}
      {...rest}
    >
      {order ? (
        <div className="question-circle-container">
          <QuestionV2 no={order} marginRight={0} testMode={testMode} />
        </div>
      ) : null}
      <StrikeDiv
        className={cl(markedStatus, 'strike-div', {
          'is-view-test': testMode === TestModeEnum.VIEW_TEST_QUESTION,
          'is-view-explanation': testMode === TestModeEnum.EXPLANATION,
        })}
        status={markedStatus}
      >
        {content}
      </StrikeDiv>
    </Container>
  );
};

const AnswerContainer = styled.div.attrs({ contentEditable: false })<any>`
  display: inline-block;
  flex-wrap: wrap;
  user-select: false;
  justify-content: flex-start;
  margin: -4px;
  align-items: center;

  .question-circle-container {
    display: inline-block;
    margin-right: 8px;
  }

  &.blank-selected {
    .missing {
      .question-circle-container {
        .circle-text {
          background-color: ${colorsV2.primary100};
          > span {
            color: ${colorsV2.white100};
          }
        }
      }
    }
    .in-correct {
      .question-circle-container {
        .circle-text {
          background-color: ${colorsV2.red100};
          > span {
            color: ${colorsV2.white100};
          }
        }
      }
    }
    .correct {
      .question-circle-container {
        .circle-text {
          background-color: ${colorsV2.green100};
          > span {
            color: ${colorsV2.white100};
          }
        }
      }
    }
  }
`;

/* stylelint-disable no-descending-specificity */
const Container = styled.div`
  display: inline-block;
  column-gap: 10px;
  grid-template-columns: max-content;
  grid-auto-flow: column;
  transition: 300ms;
  align-items: center;
  margin: 4px;

  button {
    svg {
      path {
        color: ${namedColors.jasperBrand};
        stroke-width: 1;
      }
    }

    &:hover {
      svg {
        path {
          color: ${namedColors.sweetBrown};
        }
      }
    }
  }

  &.in-correct {
    border: none;
    text-decoration: line-through ${colorsV2.primary100};

    .circle-text {
      > span {
        color: ${colorsV2.red100};
      }
    }
    .strike-div {
      color: ${colorsV2.primary100};
    }

    & button,
    & button:hover {
      svg {
        path {
          color: ${colorsV2.red100};
        }
      }
    }
  }

  &.correct {
    border: none;
    .circle-text {
      > span {
        color: ${colorsV2.green100};
      }
    }
    & button,
    & button:hover {
      svg {
        path {
          color: ${colorsV2.green100};
        }
      }
    }
  }

  &.not-answer {
    border: none;
    color: ${colorsV2.yellow100};
    & button,
    & button:hover {
      svg {
        path {
          color: ${colorsV2.yellow100};
        }
      }
    }
  }

  &.disable-result {
    border: 1px solid ${colorsV2.gray20};

    & button,
    & button:hover {
      svg {
        path {
          color: ${colorsV2.gray20};
        }
      }
    }
  }

  &.missing {
    .circle-text {
      > span {
        color: ${colorsV2.black100};
      }
    }
  }

  &.is-view-test {
    .strike-div {
      display: inline-block;
      width: 60px;
      border-bottom: 2px solid ${colorsV2.gray50};
    }
  }

  &.is-view-explanation {
    .strike-div {
      background-color: transparent;
    }
  }
`;

export default React.memo(Blank);
